import {noop} from 'lodash';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Plural, Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {UserTheme, useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {Banner, BannerTypesEnum} from '../../../common/banners/banner';
import {BannerAction} from '../../../common/banners/bannerAction';
import {BannerCloseAction} from '../../../common/banners/bannerCloseAction';
import {BannerContent} from '../../../common/banners/bannerContent';
import {BannerContentTitle} from '../../../common/banners/bannerContentTitle';
import {BannerFooterActions} from '../../../common/banners/bannerFooterActions';
import {BannerIcon} from '../../../common/banners/bannerIcon';
import {BannerPrioritiesEnum} from '../../../common/banners/bannersContext';

/*
 * Props.
 */

interface TrialEndedBannerProps {
  isGlobalAdmin: boolean;
  daysLeftInTrial: number;
  goToBilling: Handler;
  composeUpgradePlanMessageToAdmin: Handler;
}

/*
 * Style.
 */

const StyledDescriptionDiv = styled.div`
  padding-top: 4px;
  font-size: 12px;
`;

/*
 * Component.
 */

export const TrialEndedBanner: FC<TrialEndedBannerProps> = (props) => {
  const {isGlobalAdmin, daysLeftInTrial, goToBilling, composeUpgradePlanMessageToAdmin} = props;
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);
  const theme = useUserTheme();

  if (isBannerDismissed) {
    return null;
  }

  const makeOnClickWithDismissal =
    (handler: Handler = noop): Handler =>
    () => {
      setIsBannerDismissed(true);
      handler();
    };

  const onCloseClick = makeOnClickWithDismissal();
  const onUpgradeClick = makeOnClickWithDismissal(goToBilling);
  const onContactClick = makeOnClickWithDismissal(composeUpgradePlanMessageToAdmin);

  const {color, priority} = computeBannerProps(daysLeftInTrial, theme);

  return (
    <Banner bannerType={BannerTypesEnum.MULTILINE} color={color} priority={priority}>
      <BannerCloseAction onClick={onCloseClick} />
      <BannerIcon iconName="warningFilledSmall" />
      <BannerContent>
        <BannerContentTitle>{renderDaysLeftInTrial(daysLeftInTrial)}</BannerContentTitle>
        <StyledDescriptionDiv>{renderDescription(daysLeftInTrial, isGlobalAdmin)}</StyledDescriptionDiv>
      </BannerContent>
      <BannerFooterActions>
        {isGlobalAdmin ? (
          <BannerAction onClick={onUpgradeClick}>
            <Trans id="banner-trialending-action-upgradeplan">Upgrade plan</Trans>
          </BannerAction>
        ) : (
          <BannerAction onClick={onContactClick}>
            <Trans id="banner-trialending-action-contactadmin">Contact administrator</Trans>
          </BannerAction>
        )}
      </BannerFooterActions>
    </Banner>
  );
};

/*
 * Helpers.
 */

function computeBannerProps(daysLeftInTrial: number, theme: UserTheme) {
  if (daysLeftInTrial === 0) {
    return {
      color: theme.palette.red.shade40,
      priority: BannerPrioritiesEnum.ERROR,
    };
  }

  return {
    color: theme.palette.orange.shade40,
    priority: BannerPrioritiesEnum.INFORMATION,
  };
}

function renderDaysLeftInTrial(daysLeftInTrial: number) {
  return (
    <Plural
      id="banner-trialend-title-daysleftintrial"
      value={daysLeftInTrial}
      _0="Your free trial has ended"
      one="Your trial ends within 24 hours"
      other="Your trial ends in # days"
    />
  );
}

function renderDescription(daysLeftInTrial: number, isGlobalAdmin: boolean) {
  if (daysLeftInTrial === 0 && isGlobalAdmin) {
    return (
      <Trans id="banner-trialending-description-endedadmin">
        Please upgrade your plan to keep using Front.
      </Trans>
    );
  }

  if (daysLeftInTrial === 0 && !isGlobalAdmin) {
    return (
      <Trans id="banner-trialending-description-endednonadmin">
        Please ask your administrator about upgrading your plan to keep using Front.
      </Trans>
    );
  }

  if (!isGlobalAdmin) {
    return (
      <Trans id="banner-trialending-description-endingsoonnonadmin">
        Please ask your administrator about upgrading your plan to avoid service disruption.
      </Trans>
    );
  }

  return (
    <Trans id="banner-trialending-description-meetingnotscheduled">
      Please upgrade your plan or schedule a call with us to avoid service disruption.
    </Trans>
  );
}
